import { ChevronRight } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

/**
 * Maps a URL path (without leading slash) to a core.nav i18n key.
 * Dynamic id segments (UUID or numeric) are skipped during resolution.
 * Max breadcrumb depth: 3 levels (Home > Section > Page).
 */
const ROUTE_LABEL_KEYS: Record<string, string> = {
  home: 'nav_home',
  people: 'nav_people',
  'people/teams': 'nav_people_teams',
  'org-chart': 'nav_org_chart',
  inbox: 'nav_inbox',
  documents: 'nav_documents',
  search: 'search',
  calendar: 'nav_calendar',
  reminders: 'nav_reminders',
  tasks: 'nav_tasks',
  'api-keys': 'nav_api_keys',
  messages: 'messages',
  quotes: 'nav_quotes',
  tickets: 'nav_tickets',
  files: 'nav_files',
  automations: 'nav_automations',
  'automations/runs': 'nav_automations_runs',
  'automations/new': 'nav_automations_new',
  attendance: 'nav_attendance',
  vacations: 'nav_vacations',
  expenses: 'nav_expenses',
  'expenses/approvals': 'nav_expenses_approvals',
  'expenses/reimbursements': 'nav_expenses_reimbursements',
  'expenses/new': 'nav_expenses_new',
  recruitment: 'nav_recruitment',
  'hr/vacancies': 'nav_recruitment',
  'addons/contracts': 'nav_contracts',
  'addons/assets': 'nav_assets',
  'addons/training': 'nav_training',
  announcements: 'nav_announcements',
  'addons/helpdesk': 'nav_helpdesk',
  'addons/projects': 'nav_projects',
  'addons/goals': 'nav_goals',
  'addons/company-events': 'nav_company_events',
  'addons/policies': 'nav_policies',
  'addons/salary-requests': 'nav_salary_requests',
  'addons/disciplinary': 'nav_disciplinary',
  payroll: 'nav_payroll',
  schedules: 'nav_schedules',
  'schedules/manage': 'nav_schedules_manage',
  performance: 'nav_performance',
  'performance/manager': 'nav_performance_manager',
  'performance/self': 'nav_performance_self',
  'addons/crm-leads': 'nav_crm_leads',
  'addons/crm-contacts': 'nav_contacts',
  'addons/crm-deduplication': 'nav_crm_deduplication',
  'addons/crm-lead-scoring': 'nav_crm_lead_scoring',
  'addons/product-catalog': 'nav_product_catalog',
  'addons/invoicing': 'nav_invoicing',
  'addons/invoicing/customers': 'nav_invoicing_customers',
  'addons/invoicing/suppliers': 'nav_suppliers',
  'addons/time-tracking': 'nav_time_tracking',
  'addons/surveys': 'nav_surveys',
  'addons/budgets': 'nav_budgets',
  'addons/benefits': 'nav_benefits',
  'addons/leave-management': 'nav_leave',
  'addons/wfh': 'nav_wfh',
  'addons/overtime-requests': 'nav_overtime',
  'addons/procurement': 'nav_procurement',
  'procurement/requisitions': 'nav_procurement_requisitions',
  'procurement/rfq': 'nav_procurement_rfq',
  'procurement/rfq/create': 'nav_procurement_rfq_create',
  'procurement/orders': 'nav_procurement_orders',
  'procurement/orders/create': 'nav_procurement_orders_create',
  'procurement/receipts': 'nav_procurement_receipts',
  'procurement/receipts/create': 'nav_procurement_receipts_create',
  'procurement/vendors': 'nav_procurement_vendors',
  'procurement/approvals': 'nav_procurement_approvals',
  'addons/job-grades': 'nav_job_grades',
  reports: 'nav_reports',
  'reports/headcount': 'nav_reports_headcount',
  'reports/payroll': 'nav_reports_payroll',
  'reports/attendance': 'nav_reports_attendance',
  'reports/cohort': 'nav_reports_cohort',
  'reports/funnel': 'nav_reports_funnel',
  'reports/predictive': 'nav_reports_predictive',
  settings: 'settings',
  'settings/profile': 'nav_settings_profile',
  'settings/data-export': 'nav_settings_data_export',
  'settings/performance-reviews': 'nav_settings_performance_reviews',
  'settings/review-archive': 'nav_settings_review_archive',
  'settings/tenant': 'nav_settings_tenant',
  'settings/tenant/addons': 'nav_settings_addons',
  'settings/billing': 'nav_settings_billing',
  'settings/security': 'nav_settings_security',
  'settings/mfa': 'nav_settings_mfa',
  'settings/developer': 'nav_settings_developer',
  'settings/integrations': 'nav_settings_integrations',
  'settings/audit': 'audit_log',
  'settings/rules': 'nav_settings_rules',
  'settings/orientation-templates': 'nav_settings_orientation_templates',
  'settings/roles': 'nav_settings_roles',
  'settings/roles/:roleId/ui-elements': 'nav_settings_role_ui_elements',
  'reports/turnover': 'nav_reports_turnover',
  'settings/ai': 'nav_settings_ai',
  'settings/legacy': 'nav_settings_legacy',
  'settings/legacy/language': 'nav_settings_legacy_language',
  'settings/legacy/theme': 'nav_settings_legacy_theme',
  'addons/time-tracking/report': 'nav_time_tracking_report',
};

/** Segments that look like UUIDs or numeric IDs — skip them. */
const UUID_RE = /^[0-9a-f-]{8,}$/i;
const NUMERIC_RE = /^\d+$/;

function isIdSegment(s: string): boolean {
  return UUID_RE.test(s) || NUMERIC_RE.test(s);
}

interface Crumb {
  label: string;
  to: string;
}

export function AppBreadcrumbs() {
  const { t } = useTranslation('core.nav');
  const location = useLocation();

  const crumbs = useMemo<Crumb[]>(() => {
    const pathname = location.pathname.replace(/^\//, '').replace(/\/$/, '');
    if (!pathname || pathname === 'home') return [];

    const segments = pathname.split('/');
    const trail: Crumb[] = [];
    let consumed = 0;

    while (consumed < segments.length && trail.length < 3) {
      const seg = segments[consumed];
      if (seg === undefined || isIdSegment(seg)) {
        consumed++;
        continue;
      }

      // Try longest match first (up to 3 segments from current position)
      let matched = false;
      const maxLen = Math.min(segments.length - consumed, 3);
      for (let len = maxLen; len >= 1; len--) {
        const candidate = segments.slice(consumed, consumed + len).join('/');
        const key = ROUTE_LABEL_KEYS[candidate];
        if (key) {
          const label = t(key, { defaultValue: candidate });
          const to = `/${segments.slice(0, consumed + len).join('/')}`;
          const lastCrumb = trail[trail.length - 1];
          if (trail.length === 0 || lastCrumb?.to !== to) {
            trail.push({ label, to });
          }
          consumed += len;
          matched = true;
          break;
        }
      }

      if (!matched) consumed++;
    }

    return trail;
  }, [location.pathname, t]);

  if (crumbs.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1 px-4 py-1.5 text-[12px] text-[var(--color-text-secondary)] border-b border-[var(--color-border)] bg-[var(--color-bg)]"
    >
      <Link
        to="/home"
        className="hover:text-[var(--color-text-primary)] transition-colors duration-150"
      >
        {t('home')}
      </Link>
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <span key={crumb.to} className="flex items-center gap-1">
            <ChevronRight size={12} className="text-[var(--color-text-tertiary)]" aria-hidden />
            {isLast ? (
              <span className="font-medium text-[var(--color-text-primary)]" aria-current="page">
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.to}
                className="hover:text-[var(--color-text-primary)] transition-colors duration-150"
              >
                {crumb.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
